import { API } from '../constants/constants';
import { ContentLog, ContentLogRequestPayload, File, LiveLink, UpdateContentLogWithFilesMutationProps } from '../types';
import { normalizeToIds, normalizeToSubs } from '../../utils/eventHandlerUtils';

const normalizeLiveLink = (liveLink: ContentLog['liveLink']): LiveLink | null => {
  if (!liveLink?.name || !liveLink.path) {
    return null;
  }

  return {
    name: liveLink.name,
    description: liveLink.description ?? '',
    path: liveLink.path,
  };
};

export const buildContentLogPayload = (payload: ContentLog): ContentLogRequestPayload => {
  const {
    year: _year,
    month: _month,
    expert,
    audience,
    linkedService,
    lead,
    dept,
    campaign,
    type,
    ...rest
  } = payload as ContentLog & {
    year?: string;
    month?: string;
    expert?: ContentLog['experts'];
    audience?: ContentLog['audiences'];
    linkedService?: ContentLog['linkedServices'];
    lead?: ContentLog['leads'];
    dept?: ContentLog['depts'];
    campaign?: ContentLog['campaigns'];
    type?: ContentLog['types'];
  };

  const experts = payload.experts ?? expert;
  const audiences = payload.audiences ?? audience;
  const linkedServices = payload.linkedServices ?? linkedService;
  const leads = payload.leads ?? lead;
  const depts = payload.depts ?? dept;
  const campaigns = payload.campaigns ?? campaign;
  const types = payload.types ?? type;
  let normalizedDepts: ContentLogRequestPayload['depts'] = [];
  if (Array.isArray(depts)) {
    normalizedDepts = depts;
  } else if (depts) {
    normalizedDepts = [depts];
  }

  let normalizedCampaigns: ContentLogRequestPayload['campaigns'] = [];
  if (Array.isArray(campaigns)) {
    normalizedCampaigns = campaigns;
  } else if (campaigns) {
    normalizedCampaigns = [campaigns];
  }

  let normalizedTypes: ContentLogRequestPayload['types'] = [];
  if (Array.isArray(types)) {
    normalizedTypes = types;
  } else if (types) {
    normalizedTypes = [types];
  }

  return {
    ...rest,
    experts: normalizeToIds(experts),
    audiences: normalizeToIds(audiences),
    linkedServices: normalizeToIds(linkedServices),
    leads: normalizeToSubs(leads),
    depts: normalizedDepts,
    campaigns: normalizedCampaigns,
    types: normalizedTypes,
    liveLink: payload.liveLink === null ? null : normalizeLiveLink(payload.liveLink) ?? undefined,
  };
};

export const getMonthlyContentLogs = async (month: string, timezoneOffset: string) => {
  const res = await API.get('/content-logs/monthly', {
    params: {
      month,
      timezoneOffset,
    },
  });

  return res.data;
};

export const getFilteredContentLogs = async (queryString: string) => {
  const res = await API.get(`/content-logs/filter?${queryString}`);
  return res.data?.data ?? [];
};

export const getFuzzySearchContentLogs = async (queryString: string, category: string) => {
  const normalizedQuery = typeof queryString === 'string' ? queryString : '';
  const normalizedCategory = typeof category === 'string' ? category : '';
  const res = await API.get(
    `/content-logs/fuzzySearch?queryString=${encodeURIComponent(normalizedQuery)}&category=${encodeURIComponent(
      normalizedCategory
    )}`
  );
  return res.data?.data ?? [];
};

export const createContentLog = async (payload: ContentLog | ContentLogRequestPayload) => {
  const normalizedPayload = buildContentLogPayload(payload as ContentLog);
  const res = await API.post('/content-log', normalizedPayload);
  return res;
};

export const uploadContentLogFiles = async ({ id, files }: { id: string; files: globalThis.File[] }) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('file', file));

  return API.post(`/content-log/${id}/upload`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
};

export const uploadContentLogUrl = async ({
  id,
  file,
}: {
  id: string;
  file: Pick<File, 'name' | 'description' | 'path'>;
}) => {
  return API.post(`/content-log/${id}/url`, {
    url: file.path,
    name: file.name,
    description: file.description ?? '',
  });
};

export const updateContentLogWithFiles = async ({
  id,
  fields,
  uploadingFiles,
  uploadingUrls,
  removingFiles,
  removingUrls,
}: UpdateContentLogWithFilesMutationProps) => {
  const formData = new FormData();
  for (const file of uploadingFiles) {
    formData.append('file', file);
  }
  formData.append('urls', JSON.stringify(uploadingUrls));
  formData.append('deletingFiles', JSON.stringify(removingFiles));
  formData.append('deletingUrls', JSON.stringify(removingUrls));

  const normalizedFields = buildContentLogPayload(fields as ContentLog);
  formData.append('fields', JSON.stringify(normalizedFields));

  const res = await API.put(`/content-logs/${id}/update`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return res.data;
};

export const deleteContentLog = async (id: string) => {
  const res = await API.delete(`/content-log/${id}`);
  return res.data;
};

export const duplicateContentLog = async (id: string) => {
  const res = await API.post(`/content-log/${id}/duplicate`);
  return res.data;
};
